import { xc_apply } from '@Shelly/xcspec/build/index.mjs';
import type { Device } from 'src/types/Device';
import type { XCSpecResult } from 'src/types/xmod';
import { XMOD_MAX_COMPONENT_INDEX } from 'src/constants';
import { postReq } from 'src/services/http';

/**
 * Get JWS token from device info or settings.
 */
export const getJWSFromDevice = (device: Device, info?: any, settings?: any): string | undefined => {
	if (info?.jwt && typeof info.jwt === 'string') {
		return info.jwt;
	}

	if (settings?.DeviceInfo?.jwt && typeof settings.DeviceInfo.jwt === 'string') {
		return settings.DeviceInfo.jwt;
	}

	return undefined;
};

/**
 * Fetch JWS token from backend using device jti.
 */
export const fetchJWSFromBackend = async (jti: string): Promise<string | undefined> => {
	if (!jti) return undefined;

	try {
		const res: any = await postReq('device/check_jti', {
			jtis: JSON.stringify([jti.toUpperCase()]),
		});

		if (res?.isok && res?.data?.[0]) {
			const data = res.data[0];

			if (data.jwt && typeof data.jwt === 'string') {
				return data.jwt;
			}

			if (data.jws && typeof data.jws === 'string') {
				return data.jws;
			}
		}
	} catch (error) {
		console.error('Error fetching JWS from backend:', error);
	}

	return undefined;
};

/**
 * Apply xcspec to device status and return components and input ownership.
 */
export const applyXCSpecToStatus = (jws: string, status: any): XCSpecResult => {
	if (!jws || !status) {
		return { components: [], inputOwnership: {} };
	}

	if (!status.sys) {
		status.sys = {};
	}

	if (!Array.isArray(status.sys.ch)) {
		status.sys.ch = [];
	}

	try {
		const [components, inputOwnership] = xc_apply({ jws }, status);

		return { components, inputOwnership };
	} catch (error) {
		console.error('[xcspec] Full error details:', {
			error,
			jwsLength: jws?.length,
			statusKeys: status ? Object.keys(status) : [],
			hasSys: !!status?.sys,
			sysCh: status?.sys?.ch,
		});
		return { components: [], inputOwnership: {} };
	}
};

/**
 * Determine device mode from components.
 *
 * IMPORTANT (XMOD):
 *  - This mapping defines how XCSpec component types (`light:0`, `cct:0`, `switch:0`, etc.)
 *    are translated into high-level modes (`light`, `cct`, `relay`, `inputs_reader`).
 *  - Changing this logic is a **breaking change** for XMOD:
 *      - it affects mode detection across the app (controls, settings, scenes, groups),
 *      - and must be coordinated with backend / XCSpec changes.
 */
export const getModeFromComponents = (components: string[]): string | undefined => {
	if (!components || components.length === 0) {
		return undefined;
	}

	const firstComponent = components[0];
	if (!firstComponent) return undefined;

	const componentType = firstComponent.split(':')[0];

	if (componentType === 'light') {
		const hasCct = components.some((component) => component.startsWith('cct:'));
		return hasCct ? 'cct' : 'light';
	}

	const modeByComponentType: Record<string, string> = {
		switch: 'relay',
		cct: 'cct',
		rgbw: 'light',
		input: 'inputs_reader',
		cover: 'roller',
	};

	return modeByComponentType[componentType];
};

/**
 * Check if status has sys.ch array with at least one component.
 */
export const hasSysChComponents = (status: any): boolean => {
	return !!(status?.sys?.ch && Array.isArray(status.sys.ch) && status.sys.ch.length > 0);
};

/**
 * Get mode from device status components (sys.ch).
 */
export const getModeFromStatus = (status: any): string | undefined => {
	if (!hasSysChComponents(status)) {
		return undefined;
	}

	return getModeFromComponents(status.sys.ch);
};

/**
 * Determine if device has specific component type.
 */
export const hasComponent = (components: string[], componentType: string): boolean => {
	return components.some((component) => component.startsWith(`${componentType}:`));
};

/**
 * Get all components of a specific type.
 */
export const getComponentsByType = (components: string[], componentType: string): string[] => {
	return components.filter((component) => component.startsWith(`${componentType}:`));
};

/**
 * Get component count for a specific type.
 */
export const getComponentCount = (components: string[], componentType: string): number => {
	return getComponentsByType(components, componentType).length;
};

/**
 * Check if status object has component data directly (fallback when sys.ch is not available).
 */
export const hasComponentInStatus = (status: any, componentType: string): boolean => {
	if (!status || !componentType) return false;

	for (let i = 0; i < XMOD_MAX_COMPONENT_INDEX; i++) {
		const componentKey = `${componentType}:${i}`;

		if (status[componentKey] && typeof status[componentKey] === 'object') {
			return true;
		}
	}

	return false;
};

/**
 * Detect components from status object directly (when sys.ch is not available).
 */
export const detectComponentsFromStatus = (status: any): string[] => {
	if (!status) return [];

	const components: string[] = [];
	const componentTypes = ['switch', 'light', 'cct', 'rgbw', 'input'];

	for (const compType of componentTypes) {
		for (let i = 0; i < XMOD_MAX_COMPONENT_INDEX; i++) {
			const componentKey = `${compType}:${i}`;

			if (status[componentKey] && typeof status[componentKey] === 'object') {
				components.push(componentKey);
			}
		}
	}

	return components;
};
